// client/src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import * as authApi from '../api/authApi.js';

const AuthContext = createContext(null);

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true); // Still useful for initial check

    // This effect now *always* tries to fetch the user on load.
    // Success means a valid session cookie exists. Failure means no session.
    useEffect(() => {
        const checkSession = async () => {
            console.log("[AuthContext] useEffect: Checking session via fetchUserData...");
            try {
                const userData = await authApi.fetchUserData(); // Calls GET /api/auth/me
                if (userData) {
                    setUser(userData); // Set user if session is valid
                    console.log('[AuthContext] useEffect: Session valid. User set:', userData);
                } else {
                     // Should not happen if API returns error on failure, but safe fallback
                     setUser(null);
                     console.log('[AuthContext] useEffect: fetchUserData returned empty, setting user null.');
                }
            } catch (error) {
                // API call failed (e.g., 401 Unauthorized, network error) - means no valid session
                console.error("[AuthContext] useEffect: fetchUserData failed (likely no valid session):", error.message || error);
                setUser(null); // Ensure user is null if session check fails
            } finally {
                setLoading(false);
                console.log('[AuthContext] useEffect: Initial session check complete, Loading set to false.');
            }
        };

        checkSession();
    }, []); // Runs once on mount

    // Login: Calls API, then fetches user data to confirm session and update state
    const login = async (credentials) => {
        setLoading(true); // Indicate login action is happening
        console.log('[AuthContext] login function started');
        try {
            // Step 1: Call login API (backend sets HttpOnly cookie on success)
            const loginResponse = await authApi.login(credentials);
            console.log("[AuthContext] login: API Response received:", loginResponse); // { message, userId }

            if (loginResponse && loginResponse.userId) { // Check for successful login indication
                // Step 2: Immediately fetch user data using the new session cookie
                console.log("[AuthContext] login: Login successful, fetching user data to confirm session...");
                const userData = await authApi.fetchUserData();
                setUser(userData); // Set the user state
                console.log('[AuthContext] login: User set after login & fetch:', userData);
                setLoading(false);
                return userData; // Return user data
            } else {
                 // Handle cases where login API might succeed status-wise but not provide expected data
                 throw new Error("Login response did not contain expected data.");
            }
         } catch (error) {
             // Clear user state on login failure
             setUser(null);
             setLoading(false);
             console.error("Login failed in AuthContext:", error.message || error);
             throw error; // Re-throw error for the component to handle
         }
     };

     // Register function (no state changes needed here usually)
     const register = async (userData) => {
         // setLoading(true); // Optional: set loading during registration?
         try {
             const response = await authApi.register(userData);
             // setLoading(false);
             return response.data;
         } catch (error) {
             // setLoading(false);
             console.error("Registration failed:", error.message || error);
             throw error;
         }
     };

     // Activate account function (no state changes needed here usually)
     const activateAccount = async (userId, code) => { // Assuming userId, code based on authApi
         // setLoading(true); // Optional: set loading during activation?
         try {
             const response = await authApi.activateAccount(userId, code);
             // setLoading(false);
             return response.data;
         } catch (error) {
             // setLoading(false);
             console.error("Account activation failed:", error.message || error);
             throw error;
         }
     };

     // Request password reset function (no state changes needed here)
     const requestPasswordReset = async (username, language) => { // Assuming username, language based on authApi
         // setLoading(true); // Optional: set loading?
         try {
             const response = await authApi.forgotPassword(username, language);
             // setLoading(false);
             return response.data;
         } catch (error) {
             // setLoading(false);
             console.error("Password reset request failed:", error.message || error);
             throw error;
         }
     };

     // Reset password function (no state changes needed here)
     const resetPassword = async (token, newPassword) => {
         // setLoading(true); // Optional: set loading?
         try {
             const response = await authApi.resetPassword(token, newPassword);
             // setLoading(false);
             return response.data;
         } catch (error) {
             // setLoading(false);
             console.error("Password reset failed:", error.message || error);
             throw error;
         }
     };

     // Logout: Calls API (backend clears cookie), then clears local state
    const logout = async () => {
        try {
            console.log("[AuthContext] Calling backend logout API...");
            await authApi.logout(); // Backend destroys session and clears cookie via Set-Cookie
            console.log("[AuthContext] Backend logout API call successful (or finished).");
        } catch (error) {
            console.error("[AuthContext] Backend logout API call failed:", error.message || error);
        } finally {
            // Always clear local state
            setUser(null);
            console.log('[AuthContext] User set to null on logout.');
            // No need to remove from localStorage anymore
        }
    };

     // Value provided by the context
     const value = {
         user,
         loading, // Expose loading state for consumers like LoginPage, PrivateRoute
         login,
         register,
         activateAccount,
         requestPasswordReset,
         resetPassword,
         logout,
         setUser // Expose setUser if needed externally
     };

     return (
         <AuthContext.Provider value={value}>
             {children}
         </AuthContext.Provider>
     );
 };